/*
 * Tiered pricing (Staffelpreis) PDP box.
 * Renders, during an active promo, an ascending tier list ("Kaufe X, erhalte zusätzlich Y % Rabatt
 * und zahle nur Z") below the price block. Tiers are product-specific, resolved from the Datora
 * Storefront Library (`co2`) via co2.conditionalDiscounts(). The price header is Liquid-rendered; on
 * variant change the freshly server-rendered header is applied together with the recomputed rows.
 *
 * The whole box (root) is hidden unless the current variant has at least one qualifying tier, so it
 * never shows a lone promo header on non-eligible products (the block lives in the universal product
 * template and the launch is shop-global). Root visibility is owned by recompute(): tiers -> show,
 * empty/error/co2-absent/unavailable -> hide. The initial variant is read from a component-local JSON
 * (so single-default-variant PDPs work too). Quick-add is intentionally out of scope (see connectedCallback).
 *
 * Globals (classic scripts loaded earlier): subscribe / PUB_SUB_EVENTS (pubsub.js, constants.js),
 * formatPrice (datora.js, layout/theme.liquid), co2 (Datora app embed).
 */
if (!customElements.get('tiered-pricing-pdp')) {
  customElements.define(
    'tiered-pricing-pdp',
    class TieredPricingPdp extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      boundCo2Recompute = undefined;
      computeToken = 0;
      lastRenderSig = null;
      currentVariant = null;
      pendingHeaderHtml = undefined;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        // Quick-add reuses the PDP section markup with rewritten ids (quickadd-<id>); full tiered
        // support there is out of scope for this PDP ticket. Stay hidden (root ships .hidden) and bail
        // before any subscriptions/recompute so no stale initial tiers are shown.
        if (this.sectionId?.startsWith('quickadd-') || this.closest('product-info')?.dataset.originalSection) {
          return;
        }

        this.productId = Number(this.dataset.productId);
        this.productTags = this.dataset.productTags ? this.dataset.productTags.split(',') : [];
        this.tierTemplate = this.dataset.tierTemplate || '';

        this.headerEl = this.querySelector(`#TieredPricingHeader-${this.sectionId}`);
        this.listEl = this.querySelector('.tiered-pricing-pdp__list');

        this.currentVariant = this.readInitialVariant();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        this.boundCo2Recompute = this.recompute.bind(this);
        // co2.cart.initialized re-fires when the rule set changes (e.g. a discount code is applied/removed).
        window.addEventListener('co2.cart.initialized', this.boundCo2Recompute);

        this.recompute();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        if (this.boundCo2Recompute) window.removeEventListener('co2.cart.initialized', this.boundCo2Recompute);
      }

      readInitialVariant() {
        // Prefer the component-local JSON (covers single-default-variant PDPs, where the variant
        // picker — and its [data-selected-variant] — is not rendered); fall back to the picker.
        const local = this.querySelector('script[type="application/json"][data-tiered-pricing-variant]');
        const picker = document.querySelector(`#variant-selects-${this.sectionId} [data-selected-variant]`);
        const raw = (local || picker)?.textContent;
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }

      onVariantChange(event) {
        // variantChange is a global pub/sub event — only react to our own section.
        if (event.data.sectionId !== this.sectionId) return;
        // Defer the header swap so it lands together with the recomputed rows (no new-header/old-rows desync).
        this.pendingHeaderHtml = event.data.html;
        this.currentVariant = event.data.variant;
        this.recompute();
      }

      transplantHeader(parsedHtml) {
        if (!this.headerEl || !parsedHtml) return;
        const source = parsedHtml.getElementById(`TieredPricingHeader-${this.sectionId}`);
        if (source) this.headerEl.innerHTML = source.innerHTML;
      }

      async recompute() {
        if (typeof co2 === 'undefined') {
          this.hideBox();
          return;
        }
        const token = ++this.computeToken;
        const variant = this.currentVariant;
        if (!variant) {
          this.hideBox();
          return;
        }
        try {
          const tiers = await this.computeTiers(variant);
          if (token !== this.computeToken) return; // superseded by a newer compute
          const sig = JSON.stringify(tiers);
          if (sig === this.lastRenderSig) {
            // Rows unchanged, but the header (price/strike/sale state) can differ for the new variant.
            this.transplantHeader(this.pendingHeaderHtml);
            this.pendingHeaderHtml = undefined;
            return;
          }
          if (tiers.length) this.showWithRows(tiers, sig);
          else this.hideBox();
        } catch (e) {
          if (token !== this.computeToken) return;
          console.error('TieredPricingPdp:', e);
          this.hideBox();
        }
      }

      async computeTiers(variant) {
        const priceCents = variant.price;
        const salePrice = variant.compare_at_price > variant.price; // real, independent of hide_compare_prices
        const variantTags = this.normalizeTags(variant.co2_variant_tags);

        const { rule_applies = [], condition_applies = [] } =
          (await co2.conditionalDiscounts(this.productId, variant.id, this.productTags, false, salePrice, variantTags)) ||
          {};

        // Self-tier = entries present as the SAME object reference in both arrays (in-process call).
        const byQuant = new Map();
        condition_applies
          .filter((entry) => rule_applies.includes(entry))
          .filter((entry) => entry.cond_minimum === 'quant' && entry.multi_apply === 'unlimited' && entry.disc_perc > 0)
          .forEach((entry) => {
            const existing = byQuant.get(entry.cond_quant);
            if (!existing || entry.disc_perc > existing.disc_perc) byQuant.set(entry.cond_quant, entry);
          });

        const tiers = [...byQuant.values()].sort((a, b) => a.cond_quant - b.cond_quant);
        if (!tiers.length) return [];

        // Baseline = normal (non-conditional) discounted unit price; not additive — the better one counts.
        const baseline = (
          await co2.itemDiscount(this.productId, variant.id, this.productTags, priceCents, 1, false, salePrice, variantTags)
        ).discPrice;

        const round = (value) => Math.round(value + 1e-9); // matches the co2 engine exactly
        return tiers
          .map((entry) => ({
            quant: entry.cond_quant,
            percent: entry.disc_perc,
            unitPriceCents: round(priceCents * (1 - entry.disc_perc / 100)),
          }))
          .filter((tier) => tier.unitPriceCents < baseline);
      }

      showWithRows(tiers, sig) {
        if (!this.listEl) return;
        this.transplantHeader(this.pendingHeaderHtml); // apply header + rows together (atomic, no flicker)
        this.pendingHeaderHtml = undefined;
        this.listEl.innerHTML = tiers
          .map((tier) => `<li class="tiered-pricing-pdp__row">${this.buildRowHtml(tier)}</li>`)
          .join('');
        this.lastRenderSig = sig;
        this.classList.remove('hidden');
      }

      hideBox() {
        if (this.listEl) this.listEl.innerHTML = '';
        this.lastRenderSig = null; // reset so the same tiers re-render after a transient empty/error state
        this.classList.add('hidden');
      }

      // Called from product-info.js setUnavailable() (no variantChange fires for an invalid combo).
      // Clearing currentVariant stops a later co2.cart.initialized recompute from re-showing stale tiers;
      // the token bump discards any in-flight recompute; hideBox() resets lastRenderSig so returning to
      // the same valid variant re-renders.
      markUnavailable() {
        this.currentVariant = null;
        this.computeToken++;
        this.hideBox();
      }

      buildRowHtml(tier) {
        // Replace placeholders with sentinels, HTML-escape the locale text, then inject trusted spans
        // (the percent number + % sign and formatPrice() output are numeric/money values, not user
        // input) — same escape/sentinel idea as free-products.js.
        const PRICE = '__TPP_PRICE__';
        const PERCENT = '__TPP_PERCENT__';
        let percentText = '';
        let text = this.tierTemplate
          .replace(/\{\{\s*quant\s*\}\}/g, String(tier.quant))
          // Capture the percent placeholder together with an adjacent % sign on EITHER side
          // ("{{ percent }} %" or, e.g. Turkish, "%{{ percent }}").
          .replace(/(?:%\s*)?\{\{\s*percent\s*\}\}(?:\s*%)?/, (match) => {
            percentText = match.replace(/\{\{\s*percent\s*\}\}/, String(tier.percent));
            return PERCENT;
          })
          .replace(/\{\{\s*price\s*\}\}/g, PRICE);
        text = this.escapeHtml(text);
        return text
          .split(PERCENT)
          .join(`<span class="tiered-pricing-pdp__percent">${this.escapeHtml(percentText)}</span>`)
          .split(PRICE)
          .join(`<span class="tiered-pricing-pdp__price">${formatPrice(tier.unitPriceCents)}</span>`);
      }

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      normalizeTags(value) {
        if (Array.isArray(value)) return value.filter((tag) => typeof tag === 'string');
        if (typeof value !== 'string' || !value) return [];
        if (value[0] === '[') {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed.filter((tag) => typeof tag === 'string') : [];
          } catch (e) {
            return [];
          }
        }
        return [value];
      }
    }
  );
}
